import { useCallback, useContext, useEffect } from 'react';
import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop/entry-points/ApolloErrorInterop';

import {
	SPACE_VIEWS_PAGINATE_EXPERIENCE,
	SPACE_VIEWS_CHANGE_EXPERIENCE,
	SPACE_VIEWS_INITIAL_EXPERIENCE,
} from '@confluence/experience-tracker/entry-points/ExperienceName';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component';
import type { ExperienceTrackerAPI } from '@confluence/experience-tracker/entry-points/ExperienceTracker';
import type { ExperienceName } from '@confluence/experience-tracker/entry-points/ExperienceNameType';

import type { SpaceViewsSort } from './useSpaceViewsOptions';

type ExperienceTrackingParams = {
	loading: boolean;
	error: ApolloErrorInterop | undefined;
	persistenceError: ApolloErrorInterop | undefined;
	persistenceUpdateLoading: boolean;
	persistenceUpdateError: ApolloErrorInterop | undefined;
	spaceViewsSort: SpaceViewsSort;
	setSpaceViewsSort: (newSort: SpaceViewsSort) => void;
	isSpaceAdmin: boolean;
};

const stopOrSucceed = (
	name: ExperienceName,
	attributes: Record<string, any>,
	error: ApolloErrorInterop | undefined,
	experienceTracker: ExperienceTrackerAPI,
) => {
	if (error) {
		experienceTracker.stopOnError({ name, attributes, error });
	} else {
		experienceTracker.succeed({ name, attributes });
	}
};

export const useSpaceViewsExperienceTracking = ({
	loading,
	error,
	persistenceError,
	persistenceUpdateLoading,
	persistenceUpdateError,
	spaceViewsSort,
	setSpaceViewsSort,
	isSpaceAdmin,
}: ExperienceTrackingParams) => {
	const experienceTracker = useContext(ExperienceTrackerContext);

	const makeTrackedFetchMore = useCallback(
		(fetchMore: any) => () => {
			experienceTracker.start({ name: SPACE_VIEWS_PAGINATE_EXPERIENCE });
			fetchMore();
		},
		[experienceTracker],
	);

	const trackedSetSpaceViewsSort = useCallback(
		(newSort: SpaceViewsSort) => {
			experienceTracker.start({
				name: SPACE_VIEWS_CHANGE_EXPERIENCE,
				attributes: { prevSort: spaceViewsSort },
			});
			setSpaceViewsSort(newSort);
		},
		[experienceTracker, spaceViewsSort, setSpaceViewsSort],
	);

	useEffect(() => {
		experienceTracker.start({ name: SPACE_VIEWS_INITIAL_EXPERIENCE });
	}, [experienceTracker]);

	useEffect(() => {
		if (!loading && !persistenceUpdateLoading) {
			const attributes = { spaceViewsSort };

			stopOrSucceed(
				SPACE_VIEWS_INITIAL_EXPERIENCE,
				{
					spaceViewsSort,
					isSpaceAdmin,
				},
				error || persistenceError,
				experienceTracker,
			);
			stopOrSucceed(
				SPACE_VIEWS_CHANGE_EXPERIENCE,
				attributes,
				error || persistenceUpdateError,
				experienceTracker,
			);
			stopOrSucceed(SPACE_VIEWS_PAGINATE_EXPERIENCE, attributes, error, experienceTracker);
		}
	}, [
		experienceTracker,
		loading,
		error,
		persistenceError,
		persistenceUpdateLoading,
		persistenceUpdateError,
		spaceViewsSort,
		isSpaceAdmin,
	]);

	return { makeTrackedFetchMore, trackedSetSpaceViewsSort };
};
